import React, { useContext } from 'react';
import classes from './Assessments.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import t from '@/utils/Language/Assessments-translations.js';
import { Button } from '@/components';
import { useRouter } from 'next/router';
import { toastContext } from '@/context/toast.context';
import { modalContext } from '@/context/modal.context';
import useAccount from '@/hooks/useAccount';
import useMixpanel from '@/hooks/useMixpanel';
import useLog from '@/hooks/useLog';
import { isReactAssessment } from '@/utils/helpers';
import PDF from '@/utils/pdf';
import { languageContext } from '@/context/language.context';
import moment from 'moment';
import { STATUS_FINISHED, STATUS_STARTED } from '@/components/pages/assessments/util';

const colorByTag = (tag) => {
  const colors = {
    default: '#006782',
    health: '#EA5D59',
    housing: '#5CB8E6',
    food: '#88A0E6'
  };

  return colors[tag?.toLowerCase()] || colors.default;
};

const formattedDate = (date) => {
  return moment(date).format('MMM D, YYYY HH:mm a');
};

const Assessment = ({ agencyClient, assessment, userAssessment, status }) => {
  const router = useRouter();
  const { currentLanguage } = useContext(languageContext);
  const { addToast } = useContext(toastContext);
  const { enqueueModal, closeModal } = useContext(modalContext);
  const { track } = useMixpanel();
  const { log } = useLog();
  const { account } = useAccount();
  const user = account.data;
  const isFinished = status === STATUS_FINISHED;
  const url =
    (agencyClient ? `/assessments/${assessment.slug}?client=${encodeURIComponent(agencyClient.id)}` : `/assessments/${assessment.slug}?`) +
    (status === null ? '&startNew=true' : '');
  const responsesUrl = agencyClient && userAssessment ? `/clients/${agencyClient.id}/assessments/${userAssessment.id}` : false;

  const goToAssessment = (url) => {
    if (!isReactAssessment(assessment.slug)) {
      window.location.href = url;
    } else {
      router.push(url);
    }
  };

  const generatePdf = (assessment_id) => {
    enqueueModal({
      name: 'LoadingModal',
      showCancel: false,
      title: t('Your document will begin downloading to your device shortly.'),
      preventClose: true
    });
    PDF.requestPdf({
      pdfData: {
        ids: assessment_id.toString()
      },
      dataKey: 'assessment_data',
      locale: currentLanguage
    })
      .then((response) => {
        if (response?.key) {
          return PDF.pollForPdf(response.key, response.file_name);
        } else {
          throw new Error('PDF generation timed out, please try again.');
        }
      })
      .then((response) => {
        closeModal();
        if (response?.pdf_url) {
          track('Downloaded PDF', { type: 'assessment', referralOrOppId: assessment_id, source: 'client-assessments' });
          log({
            event: 'Downloaded user assessment PDF',
            message: 'single',
            object_type: agencyClient ? 'AgencyClient' : 'User',
            object_id: agencyClient ? agencyClient.id : user.id,
            via: 'button on ' + (agencyClient ? 'client info' : 'user') + ' assessments page'
          });
          window.location.href = response.pdf_url;
        } else {
          throw new Error('PDF failed to generate, please try again.');
        }
      })
      .catch((err) => {
        addToast({
          message: t(err.toString()),
          type: 'error'
        });
        closeModal();
      });
  };

  const buttonText = (() => {
    let text = '';
    switch (status) {
      case 'started':
        text = t('Continue');
        break;
      case 'finished':
        if (agencyClient && assessment.slug !== 'hiv-risk') {
          text = t('review results');
        } else if (!agencyClient && assessment.slug !== 'hiv-risk') {
          text = t('edit responses');
        } else if (assessment.slug === 'hiv-risk') {
          text = t('restart assessment');
        }
        break;
      default:
        text = t('get started');
        break;
    }
    return text + ' ';
  })();

  return (
    <li className={`${classes.card} ${isFinished ? classes.finished : ''}`}>
      <div className={classes.cardHeader}>
        <div className={classes.cardIcon} style={{ backgroundColor: colorByTag(assessment.tag) }}>
          <FontAwesomeIcon icon={['far', 'comment']} />
        </div>
        <h4 className={classes.cardTitle}>
          <a href={url} alt={assessment.title}>
            {assessment.title || assessment.slug}
          </a>
          <small>{assessment.author}</small>
        </h4>
        {userAssessment?.id && userAssessment.status == STATUS_FINISHED && (
          <div className={classes.statusDate}>
            <em>
              {t('Completed')}: {formattedDate(userAssessment.finished_at)}
            </em>
          </div>
        )}
        <span className={classes.assessmentButton}>
          {isFinished && (
            <>
              {responsesUrl && (
                <Button
                  type="primary"
                  text={t('Screener Responses')}
                  icon={{ style: 'fas', position: 'right', font: 'chevron-right' }}
                  additionalClasses={'me-2 mb-2'}
                  onButtonClick={() => goToAssessment(responsesUrl)}
                />
              )}
              <Button
                type="primary"
                text={t('Download PDF')}
                icon={{ style: 'fas', position: 'left', font: 'download' }}
                additionalClasses={'mb-2'}
                onButtonClick={() => generatePdf(userAssessment.id)}
              />
            </>
          )}
          {!isFinished && (
            <Button
              type="primary"
              text={buttonText}
              icon={{ style: 'fas', position: 'right', font: 'chevron-right' }}
              onButtonClick={() => goToAssessment(url)}
            />
          )}
        </span>
        {userAssessment?.id && userAssessment.status == STATUS_STARTED && (
          <div className={classes.statusDate}>
            <em>
              {t('Last modified')}: {formattedDate(userAssessment.updated_at)}
            </em>
          </div>
        )}
      </div>
    </li>
  );
};

export default Assessment;
